import Decimal from "decimal.js-light";
import React, { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import TroopBox from "../../../components/Boxes/TroopBox";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialog";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHook";
import useToast from "../../../hooks/useToast";
import {
  fetchBarracksBuildingInfo,
  fetchGetMe,
  fetchPlayerTroop,
} from "../../../redux/pages/barracks/barracksActions";
import { BarracksState } from "../../../redux/pages/barracks/barracksSlice";
import { PlayerTroopType } from "../../../types/troop";
import Network from "../../../utils/Network";
import { useBarracksUIContext } from "../BarracksContext";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

const MergeDialog = () => {
  const axios = new Network();
  const openToast = useToast();
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation(["barracks"]);

  const { playerTroopList }: BarracksState = useAppSelector(({ barracks }) => {
    return {
      playerTroopList: barracks.playerTroopList,
    };
  }, shallowEqual) as BarracksState;

  const barracksContext = useBarracksUIContext();
  const barracksUIProps = useMemo(() => {
    return {
      mergeDialog: barracksContext?.mergeDialog,
      setMergeDialog: barracksContext?.setMergeDialog,
      setConfirmDialog: barracksContext?.setConfirmDialog,
      setUpgradeLoading: barracksContext?.setUpgradeLoading,
    };
  }, [
    barracksContext?.mergeDialog,
    barracksContext?.setMergeDialog,
    barracksContext?.setConfirmDialog,
    barracksContext?.setUpgradeLoading,
  ]);

  const handleMerge = async (source: number, dest: number) => {
    barracksUIProps?.setUpgradeLoading?.(true);
    const response = await axios.CallApi({
      url: "/playertroop/upgrade",
      body: {
        masterPlayerTroopId: source,
        slavePlayerTroopId: dest,
      },
    });
    openToast(response);
    barracksUIProps?.setConfirmDialog?.((prev) => ({
      ...prev,
      open: false,
    }));
    if (response.ok) {
      dispatch(fetchPlayerTroop(50));
      dispatch(fetchBarracksBuildingInfo());
      barracksUIProps?.setMergeDialog?.((prev) => ({
        ...prev,
        open: false,
      }));
    }
    barracksUIProps?.setUpgradeLoading?.(false);
    dispatch(fetchGetMe());
  };

  return (
    <Dialog
      size="lg"
      onClose={() =>
        barracksUIProps?.setMergeDialog?.((prev) => ({ ...prev, open: false }))
      }
      open={!!barracksUIProps?.mergeDialog?.open}
      title={t("barracks:dialogs.merge.title")}
    >
      <div style={{ marginTop: -20 }}>
        <div className="text-center mb-3 fs-big">{t("barracks:dialogs.merge.selectHero")}</div>
        <Swiper
          style={{ marginBottom: -30 }}
          slidesPerView={3.5}
          modules={[Pagination, Navigation]}
          navigation
          breakpoints={{
            1200: {
              slidesPerView: 2.2,
            },
            1000: {
              slidesPerView: 2.5,
            },
            500: {
              slidesPerView: 1.5,
            },
            290: {
              slidesPerView: 1.1,
            },
          }}
        >
          {playerTroopList?.filter(
            (item: any) =>
              item.id !== barracksUIProps?.mergeDialog?.source &&
              item.troopId ===
                barracksUIProps?.mergeDialog?.sourceItem?.troopId &&
              item.level === barracksUIProps?.mergeDialog?.sourceItem?.level &&
              item.canMerge === true
          )?.length === 0 && (
            <div className="py-4 text-center dark-yellow">
              {t("barracks:dialogs.merge.noHeroes")}
              <br />
              {t("barracks:dialogs.merge.rules")}
            </div>
          )}
          {playerTroopList
            ?.filter(
              (item: any) =>
                item.id !== barracksUIProps?.mergeDialog?.source &&
                item.troopId ===
                  barracksUIProps?.mergeDialog?.sourceItem?.troopId &&
                item.level ===
                  barracksUIProps?.mergeDialog?.sourceItem?.level &&
                item.canMerge === true
            )
            ?.map((troop: PlayerTroopType, index: number) => (
              <SwiperSlide key={troop?.id ?? index}>
                <TroopBox
                  {...troop}
                  upgrade
                  abilities={undefined}
                  damage={
                    <div className="d-flex align-items-center">
                      {troop.damage}{" "}
                      <img
                        src="/assets/images/icons/arrow-right.svg"
                        className="mx-2"
                        style={{ width: 15 }}
                        alt=""
                      />
                      <span className="color-blue bold">
                        {typeof troop?.damage === "number" &&
                          new Decimal(troop.damage + troop.damage / 2)
                            .toDecimalPlaces(1)
                            .toString()}
                      </span>
                    </div>
                  }
                  action={
                    <Button
                      disabled={troop?.canMerge !== true}
                      onClick={() =>
                        barracksUIProps?.setConfirmDialog?.({
                          open: true,
                          submit: () =>
                            handleMerge(
                              barracksUIProps?.mergeDialog?.source ?? 0,
                              troop?.id
                            ),
                          msg: t("barracks:dialogs.merge.confirm", {
                            troop: barracksUIProps?.mergeDialog?.sourceItem?.troop?.name,
                          }),
                          title: t("barracks:labels.areYouSure"),
                        })
                      }
                      variant="primary"
                    >
                      {t("barracks:dialogs.merge.select")}
                    </Button>
                  }
                />
              </SwiperSlide>
            ))}
        </Swiper>
      </div>
    </Dialog>
  );
};

export default MergeDialog;
